import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Drawer, List, ListItemButton, ListItemText, useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { colors } from '../theme';
import { ReactComponent as UCogNetIcon } from '../assets/ucognet_icon_A_U-Gate.svg';
import LanguageSwitcher from './LanguageSwitcher';
import { useI18n } from '../i18n/I18nContext';
import { irA } from '../sections/sitio/navegar';

// `ancla` = sección de la portada; `to` = página aparte.
const NAV_ITEMS = [
  { k: 'nav.home', to: '/' },
  { k: 'nav.vision', ancla: 'vision' },
  { k: 'nav.asistentes', ancla: 'asistentes' },
  { k: 'nav.metodologia', ancla: 'metodologia' },
  { k: 'nav.team', to: '/leadership' },
  { k: 'nav.contact', to: '/contact' },
];

export default function Navbar() {
  const { t } = useI18n();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 40 });
  const location = useLocation();
  const navigate = useNavigate();

  // Desde otra página: volver a la portada y bajar a la sección cuando ya esté montada (se carga en diferido).
  const abrir = (ancla) => {
    setDrawerOpen(false);
    if (location.pathname === '/' && irA(ancla)) return;
    navigate('/');
    let intentos = 0;
    const buscar = () => {
      if (irA(ancla) || intentos++ > 30) return;
      setTimeout(buscar, 100);
    };
    setTimeout(buscar, 150);
  };

  const props = (item) => (item.ancla
    ? { onClick: () => abrir(item.ancla) }
    : { component: RouterLink, to: item.to, onClick: () => setDrawerOpen(false) });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(8,15,26,0.85)' : 'rgba(8,15,26,0.55)',
          backdropFilter: scrolled ? 'blur(16px)' : 'blur(10px)',
          borderBottom: scrolled ? `1px solid ${colors.border}` : '1px solid transparent',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', px: { xs: 2, md: 3 } }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mr: 'auto', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 }}
          >
            <Box
              sx={{
                width: 36, height: 36, borderRadius: '10px', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)',
                '&:hover': { transform: 'rotate(15deg) scale(1.08)' },
                '& svg': { width: '100%', height: '100%' },
              }}
            >
              <UCogNetIcon aria-hidden="true" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary, fontSize: '1.05rem' }}>
              U-CogNet
            </Typography>
          </Box>

          <Box component="nav" aria-label="Main navigation" sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.25, mr: 1 }}>
            {NAV_ITEMS.map((item) => {
              const active = !item.ancla && location.pathname === item.to;
              return (
                <Button
                  key={item.k}
                  {...props(item)}
                  sx={{
                    color: active ? colors.accent : colors.textSecondary,
                    fontSize: '0.84rem', fontWeight: active ? 600 : 500,
                    px: 1.1, minWidth: 'auto', whiteSpace: 'nowrap',
                    '&:hover': { color: colors.textPrimary, background: 'transparent' },
                  }}
                >
                  {t(item.k)}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
            <LanguageSwitcher />
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="small"
              sx={{ fontSize: '0.8rem', px: 1.5, minHeight: 44, whiteSpace: 'nowrap' }}
            >
              {t('nav.request_demo')}
            </Button>
          </Box>

          <IconButton
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            sx={{ display: { md: 'none' }, color: colors.textPrimary }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        aria-label="Mobile navigation"
        PaperProps={{ sx: { width: 280, background: colors.surface, borderLeft: `1px solid ${colors.border}` } }}
      >
        <List sx={{ pt: 4 }}>
          {NAV_ITEMS.map((item) => (
            <ListItemButton key={item.k} {...props(item)} selected={!item.ancla && location.pathname === item.to}>
              <ListItemText
                primary={t(item.k)}
                sx={{ '& .MuiListItemText-primary': { fontWeight: 500, fontSize: '0.95rem' } }}
              />
            </ListItemButton>
          ))}
          <Box sx={{ px: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <LanguageSwitcher />
            <Button component={RouterLink} to="/contact" variant="contained" size="small" fullWidth onClick={() => setDrawerOpen(false)}>
              {t('nav.request_demo')}
            </Button>
          </Box>
        </List>
      </Drawer>
    </>
  );
}
